import { useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate, useParams } from 'react-router'
import { useBasePath } from '@shared/routing'
import { LANGUAGES } from '../data/navigation'
import { DEFAULT_LANGUAGE } from '../i18n'

const isSupported = (code: string | undefined): code is string =>
  code != null && LANGUAGES.some((language) => language.code === code)

/** Keeps i18n in sync with the `/:lang` URL segment. Call once at the app root. */
export const useLanguageSync = () => {
  const { i18n } = useTranslation()
  const { lang } = useParams()
  const location = useLocation()
  const navigate = useNavigate()
  const { resolvePath } = useBasePath()

  useEffect(() => {
    if (!isSupported(lang)) {
      void navigate(
        { hash: location.hash, pathname: resolvePath(DEFAULT_LANGUAGE) },
        { replace: true },
      )
      return
    }
    if (i18n.language !== lang) {
      void i18n.changeLanguage(lang)
    }
  }, [i18n, lang, location.hash, navigate, resolvePath])
}

/** Current language plus a change handler that updates both the URL and i18n. */
export const useLanguage = () => {
  const { i18n } = useTranslation()
  const location = useLocation()
  const navigate = useNavigate()
  const { resolvePath } = useBasePath()

  const changeLanguage = useCallback(
    (code: string) => {
      void navigate({ hash: location.hash, pathname: resolvePath(code) })
    },
    [location.hash, navigate, resolvePath],
  )

  return { changeLanguage, language: i18n.language }
}
